import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import './shell.css';
import { BannerContext } from './banner';
import { railItems, navTree, pageGroups, pageGroupId, navLabel, rawNavLabel, setNavCaptionOverrides, setAiAuditFormNav, collectGroupLeafIds, canonMod, type Badge, type NavGroupChild, type NavItem } from './navData';
import { AI_AUDIT_FORMS, aiFormNavId } from '../features/audit/aiAuditForms';
import { NAV_VIEW_MAPPING, NAV_DASHBOARD_MAPPING } from './navPermissions';
import navRegistry from '../../public/nav-registry.json';
import { canSeeNavItem, normCaption, buildNavActionMap } from './navPermissions';
import { useAuthStore } from '../auth/store';
import { touchTab, clearTabs, sidFromToken, staleWindowFor, shouldReleaseOnPageHide } from '../auth/tabRegistry';
import { watchIdle, resetActivity } from '../auth/idleTimeout';
import { warn as warnDlg } from '../components/confirm';
import { api } from '../api/client';
import { setClientTimeZone, setActiveFormDateMode } from '../utils/datetime';
import { setLoaderSymbol, setLoaderColor, setProgressStyle, setProgressColor } from '../components/loading';
import { applyAppTheme, PENDING_THEME_SAVE_KEY } from '../theme/useAppTheme';
import { ThemeSwitcher } from '../components/ThemeSwitcher';
import { NavIcon } from '../components/NavIcon';
import { setAuditTrailConfig, type AuditFormConfig } from '../utils/auditConfig';
import { fuzzyMatch } from '../utils/fuzzyMatch';
import { HomePage } from '../features/home/HomePage';
import { ModuleListView } from '../features/modules/ModuleListView';
import { AuditTrailPage } from '../features/audit/AuditTrailPage';
import { TrendAnalysisPage } from '../features/trend/TrendAnalysisPage';
import { MdlStudiesPage } from '../features/qaqc/mdl/MdlStudiesPage';
import { AvaInsightPage } from '../features/ava/AvaInsightPage';
import { ExecutiveDashboardPage } from '../features/executive/ExecutiveDashboardPage';
import PerformanceAnalyticsPage from '../features/performance/PerformanceAnalyticsPage';
import ResultEntryExpressPage from '../features/analysis/ResultEntryExpressPage';
import ResultEntryFormulaPage from '../features/analysis/ResultEntryFormulaPage';
import NotificationCenterPage from '../features/notifications/NotificationCenterPage';
import SampleBacklogPage from '../features/backlog/SampleBacklogPage';
import JobProgressPage  from '../features/backlog/JobProgressPage';
import TestProgressPage from '../features/backlog/TestProgressPage';
import SamplingAllocationPage from '../features/sampling/SamplingAllocationPage';
import ScheduledTaskPage from '../features/sampling/ScheduledTaskPage';
import SampleRegistrationPage from '../features/sampling/SampleRegistrationPage';
import SampleReceiptNotificationPage from '../features/sampling/SampleReceiptNotificationPage';
import SampleContainerPhCheckPage from '../features/sampling/SampleContainerPhCheckPage';
import SampleInPage from '../features/sampling/SampleInPage';
import SampleOutPage from '../features/sampling/SampleOutPage';
import SampleDisposalPage from '../features/sampling/SampleDisposalPage';
import SampleInOutLogPage from '../features/sampling/SampleInOutLogPage';
import SampleLocationPage from '../features/sampling/SampleLocationPage';
import SampleDisposalHistoryPage from '../features/sampling/SampleDisposalHistoryPage';
import FieldDataTrackingPage from '../features/sampling/FieldDataTrackingPage';
import FieldDataEntryPage from '../features/sampling/FieldDataEntryPage';
import FieldDataValidationPage from '../features/sampling/FieldDataValidationPage';
import FieldDataApprovalPage from '../features/sampling/FieldDataApprovalPage';
import SampleTransferPage from '../features/sampling/SampleTransferPage';
import RegulatoryCompliancePage from '../features/settings/RegulatoryCompliancePage';
import DwrcCategoriesPage from '../features/settings/DwrcCategoriesPage';
import DwrcSubCategoriesPage from '../features/settings/DwrcSubCategoriesPage';
import UserActivityPage from '../features/settings/UserActivityPage';
import ExceptionLogPage from '../features/settings/ExceptionLogPage';
import DefaultSettingsPage from '../features/settings/DefaultSettingsPage';
import FormVisibilityPage from '../features/settings/FormVisibilityPage';
import AuditTrailConfigurationPage from '../features/settings/AuditTrailConfigurationPage';
import MorAdhocReportingPage from '../features/reporting/MorAdhocReportingPage';
import MorReportingPage from '../features/reporting/MorReportingPage';
import MorTemplateSetupPage from '../features/settings/MorTemplateSetupPage';
import SuboutListPage from '../features/subout/SuboutListPage';
import AnalysisListPage from '../features/analysis/AnalysisListPage';
import SdmsResultEntryPage from '../features/analysis/SdmsResultEntryPage';
import ReportingListPage from '../features/reporting/ReportingListPage';
import EddSpreadsheetPage from '../features/reporting/EddSpreadsheetPage';
import CustomReportBuilderPage from '../features/reporting/CustomReportBuilderPage';
import { BatchReportingPage } from '../features/reporting/BatchReportingPage';
import ExternalDataImportPage from '../features/reporting/ExternalDataImportPage';
import LimitsPage from '../features/settings/LimitsPage';
import ServerMonitorPage from '../features/servermonitor/ServerMonitorPage';
import ServerHistoryPage from '../features/servermonitor/ServerHistoryPage';
import EmailAlertsPage from '../features/settings/EmailAlertsPage';
import AppConfigPage from '../features/settings/AppConfigPage';
import ClientVersionControlPage from '../features/settings/ClientVersionControlPage';
import AlpacaAppConfigPage from '../features/settings/AlpacaAppConfigPage';
import ScreenAutoLockPage from '../features/settings/ScreenAutoLockPage';
import ActConfigPage from '../features/settings/act/ActConfigPage';
import WorkflowConfigListPage from '../features/workflow/WorkflowConfigListPage';
import TrainingRecordsPage from '../features/qaqc/training/TrainingRecordsPage';
import ControlChartPage from '../features/qaqc/controlChart/ControlChartPage';
import { MyDetailsPage } from '../features/security/MyDetailsPage';
import UsersAdminPage from '../features/security/UsersAdminPage';
import RolesAdminPage from '../features/security/RolesAdminPage';
import MacAdminPage from '../features/security/MacAdminPage';
import NavigationManagerPage from '../features/security/NavigationManagerPage';
import { PaymentTermsPage } from '../features/accounting/PaymentTermsPage';
import { RegionPage } from '../features/accounting/RegionPage';
import VersionLogPage from '../features/maintenance/VersionLogPage';
// System Management + Accounting full module (merged 2026-06-26)
import { PaymentTermsSetupPage } from '../features/accounting/PaymentTermsSetupPage';
import { AnalysisDepartmentChainPage } from '../features/settings/analysisDepartmentChain/AnalysisDepartmentChainPage';
import { CityPage } from '../features/settings/systemManagement/CityPage';
import { CompanyPage } from '../features/settings/systemManagement/CompanyPage';
import { DivisionDepartmentPage } from '../features/settings/systemManagement/DivisionDepartmentPage';
import { GlossaryPage } from '../features/settings/systemManagement/GlossaryPage';
import { MessageEditorPage } from '../features/settings/systemManagement/MessageEditorPage';
import { CountryPage } from '../features/settings/systemManagement/CountryPage';
import { StatePage } from '../features/settings/systemManagement/StatePage';
import { DepartmentPage } from '../features/settings/systemManagement/DepartmentPage';
import { PositionPage } from '../features/settings/systemManagement/PositionPage';
import { TimeZonesPage } from '../features/settings/systemManagement/TimeZonesPage';
import { CurrentLanguagePage } from '../features/settings/systemManagement/CurrentLanguagePage';

const badgeText: Record<Badge, string> = { intel: 'Intel', view: 'view', new: 'new', ai: 'AI' };

/* Nav glyph rendering (TI_EMOJI + NavIcon) MOVED to the shared
   components/NavIcon.tsx (2026-07-03) so the sidebar AND every form's
   <FormTitle> render the SAME glyph — the on-page title icon can no longer
   drift from its navigation entry. */

function initials(name?: string): string {
  if (!name) return 'AI';
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() || 'AI';
}

/**
 * v5 ALPACA Intelligence shell — rail + sidebar (brand, collapsible
 * nav-groups, BTSoft footer) + slim topbar + content. Module pages are
 * placeholders; each form is built one-by-one in later iterations.
 */
export function AppShell() {
  const navigate = useNavigate();
  const { moduleKey } = useParams();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const permissions = useAuthStore((s) => s.permissions);
  const setPermissions = useAuthStore((s) => s.setPermissions);

  // ── Self-heal stale nav permissions ──────────────────────────────────────
  // Whenever the shell mounts (or the signed-in user changes) re-fetch the
  // user's permission set so a stale localStorage entry can't outlive
  // a server-side role change.  Falls open silently on failure.
  useEffect(() => {
    if (!user) return;
    // ALWAYS re-fetch perms on mount (background) so a full page reload reflects
    // permission changes made SINCE login — e.g. a Sample Backlog dashboard newly
    // assigned via Settings → Assign Dashboard To User appears on refresh WITHOUT
    // a re-login, matching ALPACA (whose server-rendered nav reflects the latest
    // grant on the next page load). The cached perms (login inline payload, or a
    // token-only restore) render FIRST so there's no nav flash; this fetch then
    // updates them in place. `permissions` is deliberately NOT in the effect deps
    // (only [user, setPermissions]) so the setPermissions below can't loop it.
    // (Was `if (permissions != null) return;`, which cached perms until re-login —
    // so a just-granted dashboard never showed on refresh. Fixed 2026-08-31.)
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/v1/auth/me/permissions');
        const data = (res.data as {
          data: {
            isAdmin: boolean;
            roles: string;
            allowedNavViews:   Array<{ navigationView: string | null; navigationCaption: string | null }>;
            allowedDashboards: Array<{ dashboardId: string }>;
            formVisibility?: Record<string, boolean>;
            dataReviewNav?: {
              sdmsResultValidation: boolean;
              sdmsResultApproval: boolean;
              resultValidation: boolean;
              resultApproval: boolean;
              fieldDataValidation: boolean;
              fieldDataApproval: boolean;
            };
            avaEnabled?: boolean;
            navActions?: Array<{
              navigationView: string | null;
              canRead: boolean; canWrite: boolean; canCreate: boolean; canDelete: boolean;
            }>;
          };
        }).data;
        if (cancelled) return;
        setPermissions({
          isAdmin: data.isAdmin,
          roles:   data.roles,
          // Lowercase to match canSeeNavItem, which compares
          // `allowedViews.has(v.toLowerCase())` (NAV_VIEW_MAPPING values are
          // lowercased at the comparison). WITHOUT this, non-admin users had every
          // view-mapped form hidden — the Set held mixed-case NavigationView names
          // that never matched the lowercased lookup (admins bypass, so only the
          // service/admin account looked correct). Mirrors the store login path
          // (auth/store.ts) and allowedDashboards just below. (2026-08-11 fix)
          allowedViews: data.allowedNavViews
            .map(v => (v.navigationView ?? '').toLowerCase())
            .filter(Boolean),
          allowedDashboards: (data.allowedDashboards ?? [])
            .map(d => (d.dashboardId ?? '').toLowerCase())
            .filter(Boolean),
          // Captions are the GLOBAL fallback — when our NavigationView mapping
          // misses a variant (e.g. Samplecheckin_ListView_Copy_Registration vs
          // Samplecheckin_ListView_SampleRegistration), the display label still
          // matches, so the item shows for the right users.
          allowedCaptions: data.allowedNavViews
            .map(v => v.navigationCaption ?? '')
            .filter(Boolean),
          // Global "Form Setup" gate — formId → visible. ANDed with the
          // per-user permission below in canSeeNavItem (a form hidden here is
          // hidden for everyone except PROTECTED_FROM_HIDE ids).
          formVisibility: data.formVisibility ?? {},
          // Data Review nav visibility (see hiddenForms below) — dropping this
          // field here would silently re-show all 4 items on any session that
          // takes this self-heal path instead of a fresh login.
          dataReviewNav: data.dataReviewNav,
          // AVA Insight visibility — mirrors ALPACA App Config "AVA Enabled".
          avaEnabled: data.avaEnabled,
          // Per-form action permissions (New/Edit/Delete gating) from the user's
          // MAC levels — must survive the self-heal path too, else buttons the
          // MAC hides would re-appear on a token-only session restore.
          navActions: buildNavActionMap(data.navActions),
        });
      } catch {
        // 401 / network error — keep whatever's in the store
      }
    })();
    return () => { cancelled = true; };
  }, [user, setPermissions]);

  // Whether a 2nd-signature approval stage is required (Default Settings →
  // Report Signing).  Drives the "Report Approval" sidebar item: it only shows
  // when approval is on (when off, validation advances straight to delivery).
  const [reportApprovalRequired, setReportApprovalRequired] = useState(false);

  // Field Data Validation / Approval sidebar visibility — driven by
  // Settings → Default Settings (Sampling Management, NavigationItemNameID
  // 'FieldDataReview1' / 'FieldDataReview2').  When a stage's Select flag is
  // off, its nav item hides (matches GoLive RecreateNavigationItems()). Read
  // from `permissions.dataReviewNav` (inlined into the login payload) — see the
  // hiddenForms comment; NOT a post-mount fetch, which flashed the item visible.

  // Data Review workflow config (Settings → Default Settings, items 5-9) — in
  // ALPACA these DefaultSetting.Select flags are BOTH the getstatus() workflow
  // gate (AnalysisWriter.ApplySdmsSampleStatusProgressionAsync) AND what turns
  // the corresponding sidebar nav item on/off. Read from `permissions.dataReviewNav`
  // (see hiddenForms below) — inlined into the login/permissions payload so it's
  // synchronously available on first render, not fetched here separately.

  // ── Client timezone (ADR-0008 MIGRATION-TEMPORARY) ───────────────────────
  // Fetch the configured client timezone once after sign-in and hand it to the
  // shared fmtLocal* helpers so still-Central (mode-b) wire values render in the
  // viewer's local time.  Falls back to the helper default on any failure.
  // Deleted with the rest of the client-zone path once every table is UTC.
  // Concurrent-login: whether seat validation is on + how often to heartbeat
  // (from GET /api/v1/app-config, set in the effect below).
  const [heartbeatCfg, setHeartbeatCfg] = useState<{ enabled: boolean; seconds: number }>(
    { enabled: false, seconds: 60 });
  // Screen Auto Lock (ALPACA's dbo.ScreenAutoLock): minutes of USER inactivity
  // before sign-out. Null = ALPACA has no row, so the behaviour stays off.
  const [autoLockMinutes, setAutoLockMinutes] = useState<number | null>(null);
  // Lets the idle watcher call the CURRENT sign-out without re-subscribing every
  // render (handleLogout is re-created each time and declared further down).
  const handleLogoutRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      // Retry once after a short delay on failure — several of the values this
      // fetch drives fail OPEN on an unset/undefined state for UX reasons, so a
      // transient failure here isn't just "keep the old default", it silently
      // freezes those settings at the wrong value for the rest of the session
      // with no trace. A single retry covers the common transient case (brief
      // network blip right after login); a console.warn on final failure makes
      // a genuine outage diagnosable instead of invisible.
      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          // silentLoading: a background poll shouldn't flash the global loader.
          const res = await api.get('/api/v1/app-config', { silentLoading: true } as never);
          const d = (res.data as { data?: { clientTimeZone?: string; formDateModes?: Record<string, string>; loaderSymbol?: string; loaderColor?: string; progressBarStyle?: string; progressBarColor?: string; reportApprovalRequired?: boolean; auditTrailConfig?: Record<string, AuditFormConfig>; userTheme?: string; concurrentLoginEnabled?: boolean; sessionHeartbeatSeconds?: number; screenAutoLockMinutes?: number } })?.data;
          if (cancelled) return;
          setHeartbeatCfg({ enabled: d?.concurrentLoginEnabled === true, seconds: d?.sessionHeartbeatSeconds ?? 60 });
          setAutoLockMinutes(typeof d?.screenAutoLockMinutes === 'number' ? d.screenAutoLockMinutes : null);
          // Per-user theme (follows the user across devices), applied AFTER login.
          // A login-page pick (pre-auth, stashed in localStorage) wins and is saved
          // now; otherwise apply the server-saved theme (null → keep the local one).
          let pendingTheme: string | null = null;
          try { pendingTheme = localStorage.getItem(PENDING_THEME_SAVE_KEY); } catch { /* ignore */ }
          if (pendingTheme) {
            applyAppTheme(pendingTheme);
            void api.put('/api/v1/app-config/user-theme', { theme: pendingTheme },
              { silentLoading: true } as never).catch(() => { /* non-fatal */ });
            try { localStorage.removeItem(PENDING_THEME_SAVE_KEY); } catch { /* ignore */ }
          } else if (d?.userTheme) {
            applyAppTheme(d.userTheme);
          }
          if (d?.clientTimeZone) setClientTimeZone(d.clientTimeZone);
          if (d?.formDateModes) setFormDateModes(d.formDateModes);
          setReportApprovalRequired(d?.reportApprovalRequired === true);
          // Field Data Validation / Approval nav visibility is no longer read here
          // — it's inlined into the login/permissions payload (permissions.dataReviewNav)
          // so the sidebar has the right answer on first render (user report 2026-07-23).
          // Global audit-trail config (standing rule 11) — drives the per-form
          // audit button + reason-on-edit/delete app-wide.
          setAuditTrailConfig(d?.auditTrailConfig);
          // Apply the client-configured loader symbol/colour + progress-bar style/colour (§18.6).
          // Loader/progress COLOUR follows the active theme accent by DEFAULT (the CSS token
          // `--ai-loader-color` / `--ai-progress-color` fall back to `var(--brand-accent)`).
          // Only pin a literal colour when the client explicitly chose a NON-default override,
          // so changing the theme recolours the loaders too (2026-06-27).
          const BRAND_DEFAULT = '#e8670a';
          if (d?.loaderSymbol) setLoaderSymbol(d.loaderSymbol);
          if (d?.loaderColor && d.loaderColor.toLowerCase() !== BRAND_DEFAULT) setLoaderColor(d.loaderColor);
          if (d?.progressBarStyle) setProgressStyle(d.progressBarStyle);
          if (d?.progressBarColor && d.progressBarColor.toLowerCase() !== BRAND_DEFAULT) setProgressColor(d.progressBarColor);
          return;   // success — done, skip the retry
        } catch (err) {
          if (cancelled) return;
          if (attempt === 2) {
            // eslint-disable-next-line no-console
            console.warn('[AppShell] /api/v1/app-config failed after retry — client timezone, ' +
              'loader/progress config, audit-trail config, and Data Review nav visibility may ' +
              'be showing stale/default values for this session.', err);
            return;
          }
          await new Promise((r) => setTimeout(r, 500));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Concurrent-login heartbeat: keeps this session live so an active user is
  // never idle-timed-out (a 401 SESSION_EVICTED flows through the axios
  // interceptor, which signs out + shows the dialog).
  //
  // CLOSING THE BROWSER RELEASES THE LICENCE IMMEDIATELY (2026-07-25).
  //
  // Every tab of one sign-in shares ONE JWT, therefore one `sid`, therefore one
  // session row — so a naive `pagehide` beacon would revoke the session the
  // user's OTHER TABS are still using and sign them out mid-work. That is why it
  // was previously left to the idle sweep. But then a browser that was simply
  // closed held its licence for the full idle timeout, locking the next person
  // out for five minutes for no reason (reported 2026-07-25).
  //
  // `tabRegistry` resolves both: each tab claims a slot renewed by the heartbeat
  // below, and on `pagehide` a tab releases the SESSION only when no other live
  // tab of this browser remains. A crashed tab leaves a stale slot that ages out,
  // and if every tab dies at once the server-side idle sweep still reclaims the
  // session — this path only ever makes release faster, never slower.
  useEffect(() => {
    if (!user || !heartbeatCfg.enabled) return;
    const sid = sidFromToken(useAuthStore.getState().token);
    const staleMs = staleWindowFor(heartbeatCfg.seconds);

    const beat = () => {
      if (sid) touchTab(sid, staleMs);      // renew this tab's slot alongside the beat
      void api.post('/api/v1/session/heartbeat', {}, { silentLoading: true } as never).catch(() => { /* interceptor handles evict */ });
    };
    const ms = Math.max(15, heartbeatCfg.seconds) * 1000;
    beat();                       // claim liveness at once, don't wait a full interval
    const timer = setInterval(beat, ms);

    // `pagehide` (not `beforeunload`) — it is the event that actually fires on
    // mobile and on bfcache navigations. `persisted` means the page is being
    // frozen into the back/forward cache and may be restored, so it is NOT a
    // close and must not release anything.
    const onPageHide = (e: PageTransitionEvent) => {
      const token = useAuthStore.getState().token;
      // One testable decision (tabRegistry.shouldReleaseOnPageHide): skips a
      // bfcache freeze, skips when other tabs of this browser are still open.
      if (!shouldReleaseOnPageHide(
        { persisted: e.persisted, sid, hasToken: !!token }, staleMs)) return;
      // fetch(keepalive) rather than sendBeacon: the endpoint is [Authorize] and
      // sendBeacon cannot set an Authorization header. keepalive lets the request
      // outlive the page, which is the whole point here.
      try {
        void fetch(`${api.defaults.baseURL ?? ''}/api/v1/session/end`, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: '{}',
        }).catch(() => { /* best-effort; idle sweep is the backstop */ });
      } catch { /* ditto */ }
    };
    window.addEventListener('pagehide', onPageHide);

    return () => {
      clearInterval(timer);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [user, heartbeatCfg.enabled, heartbeatCfg.seconds]);

  // SCREEN AUTO LOCK — sign out after N minutes of USER inactivity, using
  // ALPACA's own dbo.ScreenAutoLock value so both products behave the same.
  //
  // ALPACA measures HTTP request inactivity, which cannot work here: the SPA
  // heartbeats every 60 s whatever the person is doing, so requests never stop
  // while a tab is open. Before this, Intelligence had NO idle sign-out at all —
  // only a "browser is gone" detector — so a tab left open overnight held its
  // concurrent-user licence until the 24-hour token expired. Tracking real user
  // activity is the faithful equivalent.
  //
  // Signing out through the SAME handler as the button is deliberate: it revokes
  // the session (releasing the licence at once) and writes the access-log ending,
  // rather than just dropping the token locally.
  useEffect(() => {
    if (!user || !autoLockMinutes || autoLockMinutes <= 0) return;
    return watchIdle(
      () => autoLockMinutes,
      () => {
        handleLogoutRef.current?.();
        // Told plainly, and on the blocking modal, so it is not mistaken for a
        // crash or a lost password — the user was signed out, on purpose.
        try {
          void warnDlg(
            `You were signed out after ${autoLockMinutes} minutes without activity. Please sign in again.`,
            { title: 'Signed out for inactivity' });
        } catch { /* provider not mounted — the redirect still happens */ }
      },
    );
  }, [user, autoLockMinutes]);

  // Runtime nav-caption overrides — reflect an admin's Navigation Manager rename in the
  // live sidebar without a code change (CLAUDE.md §0d).  Renames are stored in the shared
  // dbo.NavigationItem.NavigationCustomCaption column (the same override ALPACA displays);
  // the feed returns ONLY rows that HAVE a custom caption, so a label is overridden solely
  // when an admin set one — curated labels stay untouched otherwise.  Applies to both leaf
  // forms (AI via nav-registry, legacy via NAV_VIEW_MAPPING) and group/module headers
  // (auto-resolved from a child item's Parent → group NavigationId).
  const [, setCapTick] = useState(0);
  // Admin-set module sort: sidebar group id → sort number (drives the live group order).
  const [moduleSort, setModuleSort] = useState<Record<string, number>>({});
  // Admin-set form sort: sidebar nav id → sort number (re-orders items inside a group).
  const [formSort, setFormSort] = useState<Record<string, number>>({});
  // Bumped to re-fetch the nav-config feed immediately after a Navigation Manager save
  // (the page dispatches 'ai-nav-config-changed') — so the sidebar updates without a reload.
  const [navRefreshTick, setNavRefreshTick] = useState(0);
  useEffect(() => {
    const h = () => setNavRefreshTick(t => t + 1);
    window.addEventListener('ai-nav-config-changed', h);
    return () => window.removeEventListener('ai-nav-config-changed', h);
  }, []);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/v1/navigation/captions', { silentLoading: true } as never);
        const pairs = ((res.data as { data?: { view?: string; navigationId?: string; parent?: string; customCaption?: string; dashboardId?: string; moduleName?: string; sort?: number; originalCaption?: string }[] })?.data) ?? [];
        if (cancelled) return;

        // Build lookup tables from the feed (custom captions only; all lower-cased keys).
        const byView = new Map<string, string>();       // NavigationView → custom caption (leaf)
        const byNavId = new Map<string, string>();      // NavigationId   → custom caption (group + leaf)
        const viewToParent = new Map<string, string>(); // NavigationView → Parent (a group's NavigationId)
        const byDashboard = new Map<string, string>();  // DashboardData.Oid → custom caption
        const byModule = new Map<string, { caption?: string; sort?: number }>(); // normalized module name → meta
        const viewSort = new Map<string, number>();     // NavigationView → AISortOrder (form order)
        // Fallback: originalCaption (DB NavigationCaption) → custom caption.  Used when the
        // NavigationView-based lookup misses — e.g. legacy forms whose DB view name differs
        // from the nav-registry's AI_ view, or forms not yet in NAV_VIEW_MAPPING.
        const byCaption = new Map<string, string>();    // DB NavigationCaption.lower() → custom caption
        for (const p of pairs) {
          if (p.moduleName) { byModule.set(canonMod(p.moduleName), { caption: (p.customCaption ?? '').trim() || undefined, sort: p.sort ?? undefined }); continue; }
          const cap = (p.customCaption ?? '').trim();
          if (p.view) {
            const v = p.view.toLowerCase();
            if (p.parent) viewToParent.set(v, p.parent.toLowerCase());
            if (p.sort != null) viewSort.set(v, p.sort);
            if (cap) {
              byView.set(v, cap);
              if (p.originalCaption) byCaption.set(p.originalCaption.toLowerCase().trim(), cap);
            }
          }
          if (p.dashboardId && cap) { byDashboard.set(p.dashboardId.toLowerCase(), cap); continue; }
          if (p.navigationId && cap) byNavId.set(p.navigationId.toLowerCase(), cap);
        }

        // nav id → its primary NavigationView: AI items from nav-registry, legacy items
        // from NAV_VIEW_MAPPING (first listed view is the canonical one).
        const idToView: Record<string, string> = {};
        for (const e of (navRegistry as { items?: { id: string; view: string }[] }).items ?? []) {
          if (e.view) idToView[e.id] = e.view;
        }
        for (const [id, views] of Object.entries(NAV_VIEW_MAPPING)) {
          if (!idToView[id] && views?.[0]) idToView[id] = views[0];
        }

        const overrides: Record<string, string> = {};
        // (a) Leaf forms — apply the admin's custom caption when one exists.
        // Primary match: by NavigationView (exact); fallback: by original DB caption text.
        for (const [id, view] of Object.entries(idToView)) {
          let custom = byView.get(view.toLowerCase());
          // Fallback: DB NavigationCaption matches the sidebar's curated label.
          // Handles forms where the DB row's NavigationView ≠ nav-registry's AI_ view.
          if (!custom) {
            const label = rawNavLabel(id);
            if (label) custom = byCaption.get(label.toLowerCase());
          }
          if (custom && custom !== (rawNavLabel(id) ?? '')) overrides[id] = custom;
        }
        // (a2) Items not covered by idToView (no nav-registry entry and no NAV_VIEW_MAPPING entry):
        // scan all navTree leaves and apply byCaption when the sidebar label matches a DB original.
        for (const e of navTree) {
          if (e.kind !== 'group') continue;
          for (const lid of collectGroupLeafIds(e.group)) {
            if (overrides[lid]) continue;  // already matched by view
            const label = rawNavLabel(lid);
            if (!label) continue;
            const custom = byCaption.get(label.toLowerCase());
            if (custom && custom !== label) overrides[lid] = custom;
          }
        }
        // (b) Group / module headers — resolve from a child item's Parent (a NavigationId)
        // → custom caption.  No per-group mapping; groups with no customized DB row keep
        // their curated label.
        for (const e of navTree) {
          if (e.kind !== 'group') continue;
          let groupCaption: string | undefined;
          for (const lid of collectGroupLeafIds(e.group)) {
            const view = idToView[lid];
            const parent = view ? viewToParent.get(view.toLowerCase()) : undefined;
            const cap = parent ? byNavId.get(parent) : undefined;
            if (cap) { groupCaption = cap; break; }
          }
          if (groupCaption && groupCaption !== (rawNavLabel(e.group.id) ?? e.group.label)) {
            overrides[e.group.id] = groupCaption;
          }
        }
        // (c) Dashboard / backlog nav items — NAV_DASHBOARD_MAPPING is navId → [dashboardOid];
        // apply the dashboard's custom caption to its nav id.
        if (byDashboard.size > 0) {
          for (const [id, oids] of Object.entries(NAV_DASHBOARD_MAPPING)) {
            for (const oid of oids) {
              const custom = byDashboard.get(oid.toLowerCase());
              if (custom && custom !== (rawNavLabel(id) ?? '')) { overrides[id] = custom; break; }
            }
          }
        }
        // (d) Modules — match a sidebar GROUP to a module by normalized label, then apply the
        // admin's custom caption (relabel the group header) + sort (reorder the groups).
        const grpSort: Record<string, number> = {};
        for (const e of navTree) {
          if (e.kind !== 'group') continue;
          const m = byModule.get(canonMod(e.group.label));
          if (!m) continue;
          if (m.caption && m.caption !== (rawNavLabel(e.group.id) ?? e.group.label)) overrides[e.group.id] = m.caption;
          if (m.sort != null) grpSort[e.group.id] = m.sort;
        }
        setModuleSort(grpSort);
        // (e) Form sort — map each sidebar nav id → its form's AISortOrder (via its view), so
        // the items inside a group can be re-ordered by the admin-set sort.
        const fSort: Record<string, number> = {};
        for (const [id, view] of Object.entries(idToView)) {
          const s = viewSort.get(view.toLowerCase());
          if (s != null) fSort[id] = s;
        }
        setFormSort(fSort);
        setNavCaptionOverrides(overrides);
        setCapTick(t => t + 1);   // re-render so navLabel/navMeta pick up the new captions
      } catch {
        // keep the curated labels on 401 / network error
      }
    })();
    return () => { cancelled = true; };
  }, [user, navRefreshTick]);

  // ── "Audit Trail Log AI" per-form nav (user request 2026-07-10) ──────────────
  // Fetch which AI-owned forms have ACTUALLY written audit rows (all-time) and
  // list each as its own leaf under the "Audit Trail Log AI" group, so the
  // sidebar itself shows exactly which forms are audited in AI.  Only forms with
  // data appear (per the user's choice); the consolidated "All AI Forms" leaf
  // stays first.  Wide sinceDate → "has ever been audited", not just recently.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/v1/audit/forms',
          { params: { sinceDate: '2000-01-01' }, silentLoading: true } as never);   // yyyy-MM-dd = all-time
        if (cancelled) return;
        const list = ((res.data as { data?: { formName: string; rows: number }[] })?.data) ?? [];
        const items = list
          .filter(f => f.rows > 0 && AI_AUDIT_FORMS.has(f.formName))       // AI-owned forms that have data
          .sort((a, b) => (b.rows - a.rows) || a.formName.localeCompare(b.formName))  // most-active first, then A→Z
          .map(f => ({ id: aiFormNavId(f.formName), label: f.formName, count: f.rows }));
        setAiAuditFormNav(items);
        setCapTick(t => t + 1);   // re-render so the sidebar picks up the new leaves
      } catch {
        // leave the group with just its consolidated "All AI Forms" leaf on error
      }
    })();
    return () => { cancelled = true; };
  }, [user, navRefreshTick]);

  /** navTree with its top-level GROUP entries reordered by the admin-set module sort.
   *  Groups with a stored AISortOrder use that value as their sort key.  Groups without
   *  one keep their natural navTree order by interpolating a fractional key between their
   *  neighbouring stored-sort groups, so un-sorted modules stay in their navTree-relative
   *  positions instead of being pushed to the end.  Group SLOTS stay put
   *  (sections/separators don't move) — only which group fills each slot changes.
   *  Falls back to the original tree when no module sort is set. */
  const orderedTree = useMemo(() => {
    if (Object.keys(moduleSort).length === 0) return navTree;
    const groups = navTree.filter(e => e.kind === 'group');
    const entries = groups.map((e, i) => ({
      e,
      i,
      stored: e.kind === 'group' ? (moduleSort[e.group.id] ?? null) : null,
    }));
    // For groups without a stored sort, interpolate a fractional key between the
    // nearest stored-sort neighbours so they stay in their navTree-relative order.
    const withKeys = entries.map((item, arrIdx) => {
      if (item.stored != null) return { e: item.e, key: item.stored };
      let prevKey: number | null = null, prevIdx = -1;
      for (let j = arrIdx - 1; j >= 0; j--) {
        const s = entries[j]?.stored; if (s != null) { prevKey = s; prevIdx = j; break; }
      }
      let nextKey: number | null = null, nextIdx = entries.length;
      for (let j = arrIdx + 1; j < entries.length; j++) {
        const s = entries[j]?.stored; if (s != null) { nextKey = s; nextIdx = j; break; }
      }
      let key: number;
      if (prevKey != null && nextKey != null) {
        key = prevKey + ((arrIdx - prevIdx) / (nextIdx - prevIdx)) * (nextKey - prevKey);
      } else if (prevKey != null) {
        key = prevKey + (arrIdx - prevIdx);
      } else {
        key = (nextKey ?? entries.length) - (nextIdx - arrIdx);
      }
      return { e: item.e, key };
    });
    const sorted = withKeys.sort((a, b) => a.key - b.key).map(x => x.e);
    let gi = 0;
    return navTree.map(e => (e.kind === 'group' ? sorted[gi++]! : e));
  }, [moduleSort]);

  /** Re-order a group's flat items by the admin-set form sort (items with a sort first,
   *  ascending; unsorted keep their original order).  No-op when no form sort is set. */
  function sortItems(items: NavItem[]): NavItem[] {
    if (Object.keys(formSort).length === 0) return items;
    return items
      .map((it, i) => ({ it, key: formSort[it.id] != null ? formSort[it.id]! : 100000 + i }))
      .sort((a, b) => a.key - b.key)
      .map(x => x.it);
  }

  /** Re-order the ITEM children of a group/sub-group by form sort, keeping sub-heads and
   *  sub-groups in their slots (only which item fills each item slot changes). */
  function sortChildren(children: NavGroupChild[]): NavGroupChild[] {
    if (Object.keys(formSort).length === 0) return children;
    const sorted = children
      .filter(c => c.kind === 'item')
      .map((c, i) => ({ c, key: c.kind === 'item' && formSort[c.item.id] != null ? formSort[c.item.id]! : 100000 + i }))
      .sort((a, b) => a.key - b.key)
      .map(x => x.c);
    let ii = 0;
    return children.map(c => (c.kind === 'item' ? sorted[ii++]! : c));
  }

  /** XAF NavigationView ids the current user can see. Empty Set when the
   *  user hasn't been hydrated yet (we fall open in that case — pages still
   *  show, which is correct for a freshly-mounted shell awaiting login data). */
  const allowedViews = useMemo(
    () => new Set((permissions?.allowedViews ?? []).map(v => v.toLowerCase())),
    [permissions],
  );
  /** Dashboard Oids the current user can see (Sample Backlog / ICM). */
  const allowedDashboards = useMemo(
    () => new Set((permissions?.allowedDashboards ?? []).map(s => s.toLowerCase())),
    [permissions],
  );
  /** Normalized NavigationCaption strings — global label-based fallback. */
  const allowedCaptions = useMemo(
    () => new Set((permissions?.allowedCaptions ?? []).map(normCaption)),
    [permissions],
  );
  const isAdmin = permissions?.isAdmin ?? false;

  /** Globally-hidden form ids — the admin "Form Setup" gate.  A form mapped to
   *  `false` is hidden for EVERYONE (ANDed with per-user permission), except
   *  PROTECTED_FROM_HIDE ids which canSeeNavItem keeps on regardless. */
  const hiddenForms = useMemo(
    () => {
      const s = new Set(
        Object.entries(permissions?.formVisibility ?? {})
          .filter(([, v]) => v === false)
          .map(([k]) => k),
      );
      // Report Approval is a workflow stage that only exists when a 2nd signature
      // is required — hide it from the sidebar when approval is off (Default
      // Settings → Report Signing → Require Approval = Off).
      if (!reportApprovalRequired) s.add('repappr');
      // Data Review items 6-9 (Default Settings → SDMS Result Validation / SDMS
      // Result Approval / Result Validation / Result Approval) — ALPACA parity:
      // these are BOTH the getstatus() workflow gate AND the nav-visibility flag
      // (dbo.DefaultSetting.Select) for the SAME row in legacy. Read from
      // `permissions.dataReviewNav` — inlined into the login/permissions
      // payload server-side (AuthController.BuildPermissionsPayloadAsync), NOT
      // a separate post-mount app-config fetch. Two earlier attempts at a
      // fetch-then-render approach (fail open, then fail closed) both showed a
      // visible glitch on first load — items flashing then narrowing, or the
      // whole group appearing late (user report 2026-07-17, both directions).
      // Inlining removes the loading window entirely: `permissions` is either
      // present with the real answer, or not yet loaded (in which case
      // `allowedViews.size === 0` etc. already fails the WHOLE sidebar open a
      // few lines below — consistent with how every other nav item behaves
      // before permissions arrive).
      const dr = permissions?.dataReviewNav;
      if (dr && dr.sdmsResultValidation !== true) s.add('sdmsval');
      if (dr && dr.sdmsResultApproval !== true)   s.add('sdmsappr');
      if (dr && dr.resultValidation !== true)     s.add('rval');
      if (dr && dr.resultApproval !== true)       s.add('rappr');
      // Field Data Validation / Approval — Settings → Default Settings
      // (Sampling Management, FieldDataReview1 / FieldDataReview2). Off = hide
      // the nav item, matching GoLive RecreateNavigationItems() exactly. Read
      // from `permissions.dataReviewNav` (inlined into the login payload) for the
      // SAME reason as the four items above — the previous post-mount app-config
      // fetch with a fail-open useState(true) default made the Field Data Approval
      // item flash visible on login and hide only once that fetch resolved (user
      // report 2026-07-23).
      if (dr && dr.fieldDataValidation !== true) s.add('fielddata-validation');
      if (dr && dr.fieldDataApproval   !== true) s.add('fielddata-approval');
      return s;
    },
    [permissions, reportApprovalRequired],
  );

  /** True when this nav item should appear in the sidebar.  Takes the label
   *  so the caption fallback can do label-vs-NavigationCaption matching when
   *  no explicit NavigationView mapping covers the user's permitted views.
   *  Username threaded through so SERVICE_ONLY items (User Activity) can be
   *  gated to the operator account.  hiddenForms applies the global Form Setup
   *  gate (ANDed with the per-user permission). */
  const canSee = (id: string, label = '') => {
    // AVA Insight mirrors the ALPACA App Config "AVA Enabled" toggle — the
    // same switch that shows/hides the AVA button in the ALPACA header.
    // Applies to everyone (admins included), exactly like ALPACA. Missing
    // value (older payload) fails open.
    if (id === 'avainsight' && permissions?.avaEnabled === false) return false;
    return canSeeNavItem(id, label, allowedViews, allowedDashboards, allowedCaptions, isAdmin, user?.name ?? '', hiddenForms);
  };

  /** True if the given group child contains at least one item the user
   *  can see (recurses into sub-groups). Used to hide empty
   *  groups/sub-groups so they don't show as ugly empty rows. */
  function hasVisibleLeaves(c: NavGroupChild): boolean {
    if (c.kind === 'subhead') return false;
    if (c.kind === 'item')    return canSee(c.item.id, c.item.label);
    return c.subgroup.children.some(hasVisibleLeaves);
  }
  /** True if a whole top-level group has anything visible (either its flat
   *  `items` list or its structured `children` tree). */
  function groupHasVisibleLeaves(g: { items: { id: string; label: string }[]; children?: NavGroupChild[] }): boolean {
    if (g.items.some(it => canSee(it.id, it.label))) return true;
    if (g.children && g.children.some(hasVisibleLeaves)) return true;
    return false;
  }

  /** Rail icons are MODULE shortcuts — show one when ANY form of its module
   *  is visible to this user.  The previous single-anchor check (canSee on
   *  the rail id alone) wrongly hid e.g. the Sample Backlog icon for a user
   *  granted Backlog 01 but not Missing COC (the icon's hard-wired target),
   *  and the Sampling Management icon when only its anchor page was switched
   *  off in Form Setup (user report 2026-07-24). */
  function railVisible(id: string, title: string): boolean {
    for (const e of navTree) {
      if (e.kind !== 'group') continue;
      if (collectGroupLeafIds(e.group).includes(id)) return groupHasVisibleLeaves(e.group);
    }
    return canSee(id, title);   // top-level items (Home, Executive Dashboard, AVA)
  }

  // Form is reflected in the URL (/m/<key>) so each browser tab can sit on
  // its own form — refresh-safe, duplicable, back/forward works.
  const [page, setPage] = useState(() => moduleKey ?? 'home');
  // Page-scoped top-bar slots (BO UI Standard §5) — the active page can dock a
  // workflow stepper (setTopbarCenter), page badges (setTopbarRight) or a banner.
  // COEXISTS with the legacy #ai-topbar-stepper portal (SDMS / QC Batch keep that);
  // MDL Studies uses these context slots. Cleared whenever the routed page changes.
  const [banner, setBanner] = useState<ReactNode | null>(null);
  const [topbarRight, setTopbarRight] = useState<ReactNode | null>(null);
  const [topbarCenter, setTopbarCenter] = useState<ReactNode | null>(null);
  const bannerApi = useMemo(() => ({ setBanner, setTopbarRight, setTopbarCenter }), []);
  useEffect(() => { setBanner(null); setTopbarRight(null); setTopbarCenter(null); }, [page]);
  // Per-form date-source overrides (App Config, ADR-0008 MIGRATION-TEMPORARY) —
  // formId → 'utc' | 'client'.  Loaded from /api/v1/app-config after login.
  const [formDateModes, setFormDateModes] = useState<Record<string, string>>({});
  // Apply the active form's override BEFORE its date columns render (a module
  // setter, not React state, so it must run here in render — not an effect — or
  // the already-rendered columns would keep the previous mode).  Default 'auto'.
  setActiveFormDateMode((formDateModes[page] as 'utc' | 'client' | undefined) ?? 'auto');
  // Bumped when the user re-clicks the nav item they're already on.  Folded
  // into the page component's `key` so the page REMOUNTS (resetting any
  // detail/drill-down view back to its default list) — e.g. clicking "MOR
  // Adhoc Reporting" while viewing a saved report returns to the saved list
  // (user feedback 2026-05-28).
  const [reloadKey, setReloadKey] = useState(0);
  // Custom Reporting drill-down: when the user clicks a JobID on the list,
  // we don't change `page` (keeps the sidebar highlight on Custom Reporting);
  // we just remember which Job is open + flip into detail mode.  Clearing
  // this state returns to the list.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({ 'grp-backlog': false });
  const [query, setQuery] = useState('');
  const [drawer, setDrawer] = useState(false);
  // Desktop sidebar collapse state — user feedback 5/30/2026 #12: add a
  // hamburger to hide the left nav for more content space.  Persisted to
  // localStorage so the user's choice survives reloads.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('sidebarCollapsed') === 'true'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed)); } catch { /* ignore */ }
  }, [sidebarCollapsed]);
  // Allow any page to collapse the nav (e.g. Result Entry Express collapses it
  // when entering the detail view, to give the entry grid more horizontal room).
  useEffect(() => {
    const handler = () => setSidebarCollapsed(true);
    window.addEventListener('ai:nav-collapse', handler);
    return () => window.removeEventListener('ai:nav-collapse', handler);
  }, []);

  const term = query.toLowerCase().trim();
  const title = navLabel(page, 'Home');
  const av = initials(user?.name);

  // Sync state ← URL (direct load, new tab, back/forward). Skip /preview.
  useEffect(() => {
    if (location.pathname === '/preview') return;
    const target = moduleKey ?? 'home';
    setPage((p) => (p === target ? p : target));
  }, [moduleKey, location.pathname]);

  // Keep the nav group that contains the active form EXPANDED, so the user can
  // always see which form is currently open (user feedback 2026-05-28).  Only
  // adds the active group — never collapses ones the user opened manually.
  useEffect(() => {
    const gid = pageGroupId[page];
    if (gid) setOpenGroups((g) => (g[gid] ? g : { ...g, [gid]: true }));
  }, [page]);

  // ── Navigation tracking ───────────────────────────────────────────────────
  // Fire-and-forget audit of which screen the user opened.  Lands in
  // AI_UserActivityLog as EventType='Navigation', Detail='Group · Page'.
  // Deduped via a ref so re-renders on the same page don't spam the log, and
  // gated on an authenticated user.  Errors are swallowed (best-effort audit —
  // a tracking POST must never disrupt the user's navigation).
  const lastTrackedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user) return;
    if (location.pathname === '/preview') return;     // design-review only
    if (lastTrackedRef.current === page) return;       // already logged this page
    lastTrackedRef.current = page;
    const caption = navLabel(page, page === 'home' ? 'Home' : page);
    const group   = pageGroups[page] ?? '';
    api.post('/api/v1/activity/track', {
      page:   caption,
      group,
      action: 'Navigation',
    }).catch(() => { /* best-effort */ });
  }, [page, user, location.pathname]);

  const open = (id: string) => {
    // Re-clicking the active nav item resets that page to its default view.
    if (id === page) setReloadKey((k) => k + 1);
    setPage(id);
    setDrawer(false);
    if (location.pathname !== '/preview') {
      const url = id === 'home' ? '/' : `/m/${id}`;
      if (location.pathname !== url) navigate(url);
    }
  };

  // Nav items are real <a href> anchors so middle-click / Ctrl+click /
  // Cmd+click and right-click → "Open in new tab" all work natively. Plain
  // left-click intercepts to do SPA-internal navigation (no full reload).
  const hrefFor = (id: string) => id === 'home' ? '/' : `/m/${id}`;

  // A rail icon is a MODULE shortcut: opening it must land on a form the user
  // can actually SEE — the first VISIBLE leaf of its module — not the icon's
  // hard-wired anchor form.  The anchor can be a form this user isn't granted
  // (e.g. the Sampling Management icon's anchor is Field Data Tracking, but a
  // user granted only Field Data Entry must open Entry) — otherwise the icon
  // opens an unauthorized/hidden form (user report 2026-07-24).
  const railTarget = (id: string): string => {
    for (const e of navTree) {
      if (e.kind !== 'group') continue;
      const leaves = collectGroupLeafIds(e.group);
      if (!leaves.includes(id)) continue;
      // Prefer the anchor itself when the user can see it; else the first
      // visible leaf of the same module; else fall back to the anchor.
      if (canSee(id)) return id;
      const firstVisible = leaves.find(lid => canSee(lid));
      return firstVisible ?? id;
    }
    return id;   // standalone Overview items (home / execdash / avainsight)
  };

  // A rail icon stays highlighted for ANY form in its module — match the
  // current page's nav group to the rail item's group (pageGroupId).  Falls
  // back to an exact-id match for the standalone Overview items (home / exec /
  // ava) which aren't inside a nav group.  Previously the rail highlighted only
  // when the page WAS the module's first form.
  const railActive = (railId: string) => {
    if (page === railId) return true;
    const g = pageGroupId[page];
    return !!g && g === pageGroupId[railId];
  };
  const onNavClick = (e: React.MouseEvent, id: string) => {
    // Let the browser take over for modifier-clicks (new tab / new window).
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    open(id);
  };
  const toggleGroup = (id: string) => setOpenGroups((g) => ({ ...g, [id]: !g[id] }));
  // Fire-and-forget Logout audit BEFORE clearing the token.
  //
  // The JWT is attached HERE, synchronously, instead of being left to the axios
  // request interceptor.  Interceptors run in a MICROTASK, so the `logout()`
  // below — which is synchronous — used to clear the token first and the call
  // went out with no Authorization header, was rejected 401, and the `.catch`
  // swallowed it.  That is why production held 533 Login rows and ZERO Logout
  // rows (GoLive, 2026-06-01 → 07-25), and why a licence was never released on
  // sign-out (it had to wait out the 5-minute idle timeout).  Pinned by
  // logoutOrdering.test.ts.  The interceptor leaves this header alone once the
  // store is empty (`if (token)`), so no await — and no delay — is needed.
  const handleLogout = () => {
    const bearer = useAuthStore.getState().token;
    // Forget this browser's tab slots: the server has revoked the row, and a
    // leftover slot would make the NEXT sign-in think another tab is still open.
    const sid = sidFromToken(bearer);
    if (sid) clearTabs(sid);
    api.post('/api/v1/auth/logout', {}, {
      headers: bearer ? { Authorization: `Bearer ${bearer}` } : undefined,
    }).catch(() => { /* best-effort */ });
    logout();
    // Clear the activity clock so a stale timestamp cannot immediately sign the
    // NEXT user out the moment they sign in on this browser.
    resetActivity();
    navigate('/login', { replace: true });
  };
  handleLogoutRef.current = handleLogout;

  // group ids that contain a search hit (auto-expand while filtering)
  const matchedGroups = useMemo(() => {
    if (!term) return null;
    const s = new Set<string>();
    for (const e of navTree) {
      if (e.kind === 'group' && (fuzzyMatch(term, e.group.label)
        || e.group.items.some((i) => fuzzyMatch(term, i.label)))) s.add(e.group.id);
    }
    return s;
  }, [term]);

  /** Recursively render a sub-group child. `depth` controls indentation —
   *  depth 1 = direct child of top-level group, depth 2 = nested sub-folder, etc. */
  function renderNavChild(c: NavGroupChild, key: number | string, depth: number): React.ReactNode {
    if (c.kind === 'subhead') {
      return <div key={`sh${key}`} className="nav-subhead">{c.label}</div>;
    }
    if (c.kind === 'item') {
      const it = c.item;
      if (!canSee(it.id, it.label)) return null;
      return (
        <a key={it.id} href={hrefFor(it.id)}
          className={`ni sub nav-depth-${depth}${page === it.id ? ' active' : ''}`}
          onClick={(ev) => onNavClick(ev, it.id)}>
          <NavIcon icon={it.icon} color={it.iconColor} />
          {navLabel(it.id, it.label)}
          {it.badge && <span className={`badge b-${it.badge}`}>{badgeText[it.badge]}</span>}
          {it.count != null && <span className="ni-count" title={`${it.count.toLocaleString()} audit entries`}>{it.count.toLocaleString()}</span>}
        </a>
      );
    }
    // subgroup — collapsible folder, can contain further nested children
    const sg = c.subgroup;
    // Hide sub-groups where every descendant item is permission-gated away
    // (recursive — also hides parent of all-empty sub-sub-groups).
    if (!sg.children.some(hasVisibleLeaves)) return null;
    const sgOpen = !!openGroups[sg.id];
    return (
      <div key={sg.id} className="nav-subgroup">
        <button
          className={`nav-subgroup-hdr nav-depth-${depth}${sgOpen ? ' open' : ''}`}
          onClick={() => toggleGroup(sg.id)}
        >
          {/* +/- expand toggle (prototype .exp) — leftmost; '+' collapsed, '–' open. */}
          <span className="exp" aria-hidden="true" />
          <NavIcon icon={sg.icon} color={sg.iconColor} cls="group-icon" />
          <span>{sg.label}</span>
          {sg.badge && <span className={`badge b-${sg.badge}`} style={{ marginLeft: 'auto', marginRight: 6 }}>{badgeText[sg.badge]}</span>}
        </button>
        <div className={`nav-subgroup-children${sgOpen ? ' open' : ''}`}>
          {sortChildren(sg.children).map((cc, ci) => renderNavChild(cc, `${sg.id}-${ci}`, depth + 1))}
        </div>
      </div>
    );
  }

  return (
    <div className="shell">
      {/* Rail */}
      <div className="rail">
        <div className="rail-logo"><i className="ti ti-brain" /></div>
        {railItems.map((r) => {
          if (r.sep) return <div key={r.id} className="rs" />;
          if (!railVisible(r.id, r.title)) return null;
          const target = railTarget(r.id);
          return (
            <a
              key={r.id}
              href={hrefFor(target)}
              className={`ri${railActive(r.id) ? ' active' : ''}`}
              data-title={r.title}
              onClick={(e) => onNavClick(e, target)}
            >
              <NavIcon icon={r.icon} color={r.color} />
            </a>
          );
        })}
        <button className="rav" title={user?.name ?? 'Account'} onClick={handleLogout}>{av}</button>
      </div>

      {drawer && <div className="sb-backdrop show" onClick={() => setDrawer(false)} />}

      {/* Sidebar — `collapsed` class drives the desktop hide animation;
          `open` slides the mobile drawer overlay. */}
      <div className={`sidebar${drawer ? ' open' : ''}${sidebarCollapsed ? ' collapsed' : ''}`}>
        <div className="sb-brand">
          <div className="sb-brand-logo"><img src={`${import.meta.env.BASE_URL}alpaca-logo.jpg`} alt="ALPACA" /></div>
          <div className="sb-brand-text">
            <div className="sb-brand-name">ALPACA <span className="accent">Intelligence</span></div>
            <div className="sb-brand-sub">Lab Intelligence Platform</div>
          </div>
        </div>

        <div className="sb-hdr">
          <div className="sb-hdr-row">
            <span className="sb-title">Navigation</span>
          </div>
          <div className="sb-search">
            <i className="ti ti-search" />
            <input
              type="text" placeholder="Search anything…" value={query}
              aria-label="Search navigation" onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        <nav className="sb-scroll" aria-label="Primary">
          {(() => {
            // Pre-pass: render every nav entry to a React node OR null. Then
            // strip orphan separators (leading, trailing, consecutive) so an
            // entirely-hidden group doesn't leave behind two stacked dividers.
            const rendered = orderedTree.map((e, i): { node: React.ReactNode; isSep: boolean; isSection: boolean; key: string } => {
              if (e.kind === 'sep') {
                return { node: term ? null : <div key={`s${i}`} className="sep" />, isSep: true, isSection: false, key: `s${i}` };
              }
              if (e.kind === 'section') {
                return { node: term ? null : <div key={`l${i}`} className="sl">{e.label}</div>, isSep: false, isSection: true, key: `l${i}` };
              }
              if (e.kind === 'item') {
                if (term && !fuzzyMatch(term, e.item.label)) return { node: null, isSep: false, isSection: false, key: `i${i}` };
                if (!canSee(e.item.id, e.item.label))                     return { node: null, isSep: false, isSection: false, key: `i${i}` };
                return {
                  node: (
                    <a key={e.item.id} href={hrefFor(e.item.id)}
                      className={`ni${page === e.item.id ? ' active' : ''}`}
                      onClick={(ev) => onNavClick(ev, e.item.id)}>
                      <NavIcon icon={e.item.icon} />{navLabel(e.item.id, e.item.label)}
                      {e.item.badge && <span className={`badge b-${e.item.badge}`}>{badgeText[e.item.badge]}</span>}
                      {e.item.count != null && <span className="ni-count" title={`${e.item.count.toLocaleString()} audit entries`}>{e.item.count.toLocaleString()}</span>}
                    </a>
                  ), isSep: false, isSection: false, key: e.item.id };
              }
              // group
              const g = e.group;
              const isOpen = term ? !!matchedGroups?.has(g.id) : !!openGroups[g.id];
              // Permission-gate flat items + structured children together.
              if (!groupHasVisibleLeaves(g))                          return { node: null, isSep: false, isSection: false, key: g.id };
              if (term && !fuzzyMatch(term, g.label)
                && !g.items.some(it => fuzzyMatch(term, it.label))) {
                return { node: null, isSep: false, isSection: false, key: g.id };
              }
              const visibleItems = g.items.filter(it => canSee(it.id, it.label));
              const items = sortItems(term ? visibleItems.filter(it => fuzzyMatch(term, it.label)) : visibleItems);
              const useChildren = !term && Array.isArray(g.children) && g.children.length > 0;
              return {
                node: (
                  <div key={g.id}>
                    {!term && (
                      <button className={`nav-group-hdr${isOpen ? ' open' : ''}`} onClick={() => toggleGroup(g.id)}>
                        {/* +/- expand toggle (prototype .exp) — leftmost; '+' collapsed, '–' open. */}
                        <span className="exp" aria-hidden="true" />
                        <NavIcon icon={g.icon} color={g.iconColor} cls="group-icon" />
                        <span>{navLabel(g.id, g.label)}</span>
                        {g.badge && <span className={`badge b-${g.badge}`} style={{ marginLeft: 'auto', marginRight: 6 }}>{badgeText[g.badge]}</span>}
                      </button>
                    )}
                    <div className={`nav-group-children${isOpen ? ' open' : ''}`}>
                      {useChildren
                        ? sortChildren(g.children!).map((c, ci) => renderNavChild(c, ci, 1))
                        : items.map((it) => (
                            <a key={it.id} href={hrefFor(it.id)}
                              className={`ni sub${page === it.id ? ' active' : ''}`}
                              onClick={(e) => onNavClick(e, it.id)}>
                              <NavIcon icon={it.icon} color={it.iconColor} />
                              {navLabel(it.id, it.label)}
                              {it.badge && <span className={`badge b-${it.badge}`}>{badgeText[it.badge]}</span>}
                              {it.count != null && <span className="ni-count" title={`${it.count.toLocaleString()} audit entries`}>{it.count.toLocaleString()}</span>}
                            </a>
                          ))}
                    </div>
                  </div>
                ), isSep: false, isSection: false, key: g.id };
            });

            // Drop orphan separators: leading, trailing, or right after another
            // separator / section header (which already provides visual break).
            const out: React.ReactNode[] = [];
            let lastWasBreak = true;   // start "broken" so a leading sep gets dropped
            for (const r of rendered) {
              if (r.node === null) continue;
              if (r.isSep) {
                if (lastWasBreak) continue;
                out.push(r.node);
                lastWasBreak = true;
              } else {
                out.push(r.node);
                lastWasBreak = r.isSection;
              }
            }
            // Strip trailing separator if any sneaked in.
            while (out.length > 0) {
              const last = out[out.length - 1] as React.ReactElement;
              if (last?.props?.className === 'sep') out.pop();
              else break;
            }
            return out;
          })()}
        </nav>

        <div className="sb-footer" title="Powered by BTSoft · 2026">
          <div className="sb-footer-by">Powered by</div>
          <img src={`${import.meta.env.BASE_URL}btsoft-logo.jpg`} alt="BTSoft" className="sb-footer-logo-img" />
          <span className="sb-footer-ver">v26.8.12</span>
        </div>
      </div>

      {/* Main */}
      <div className="main">
        <div className="topbar">
          <div className="tb-left">
            {/* Hamburger — on desktop (>860px) collapses the sidebar to
                give more screen for data.  On mobile it still toggles the
                drawer overlay.  User feedback 5/30/2026 #12. */}
            <button
              className="hamb"
              aria-label="Toggle navigation"
              title={sidebarCollapsed ? 'Show navigation' : 'Hide navigation'}
              onClick={() => {
                if (window.innerWidth <= 860) setDrawer(d => !d);
                else                          setSidebarCollapsed(c => !c);
              }}
            ><i className="ti ti-menu-2" /></button>
            <i className="ti ti-home" style={{ fontSize: 14, color: 'var(--ai-muted)' }} />
            <span className="topbar-title">{title}</span>
          </div>
          {/* Docked workflow-stepper slot (BO UI Standard §5) — centred in the
              top bar.  SDMS / QC Batch portal their <StepIsland> into
              #ai-topbar-stepper (unchanged); MDL Studies renders {topbarCenter}
              here via the banner context.  They are mutually exclusive per page,
              so both share this one centred slot without conflict. */}
          <div id="ai-topbar-stepper" className="tb-stepper-slot">{topbarCenter}</div>
          <div className="tb-right">
            {topbarRight}
            {/* Top-right alpaca logo removed (user request 2026-07-09). */}
            <span className="vtag">v26.8.12</span>
            <ThemeSwitcher />
            <button className="tb-btn"><i className="ti ti-bell" /></button>
            {/* ALPACA parity: show the logged-in user's display name (the
                Employee DisplayName / E-code, same as the legacy header). */}
            {user?.name && <span className="tb-user" title={user.email}>{user.name}</span>}
            <button className="tav" title="Sign out" onClick={handleLogout}>{av}</button>
          </div>
        </div>

        <BannerContext.Provider value={bannerApi}>
        {/* Page-docked banner (BO UI Standard §5) — e.g. MDL below-topbar strip. */}
        {banner && <div className="topbanner-slot">{banner}</div>}
        <div className="content">
          {page === 'home' ? (
            <div className="page"><HomePage onOpen={open} canSee={canSee} /></div>
          ) : page === 'execdash' ? (
            <ExecutiveDashboardPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page.startsWith('pa-') ? (
            // Performance Analytics — any `pa-<dashId>` nav item renders that
            // dashboard; the category/group nav lives in the left sidebar.
            <PerformanceAnalyticsPage key={`${page}-${reloadKey}`} dashId={page.slice(3)} onOpen={(id) => open('pa-' + id)} />
          ) : page.startsWith('nc-') ? (
            // Notification Center — any `nc-<subId>` nav item renders that
            // sub-page; the module nav lives in the left sidebar (like PA).
            <NotificationCenterPage key={`${page}-${reloadKey}`} subId={page.slice(3)} onOpen={(id) => open('nc-' + id)} />
          ) : page === 'samplebacklog' || page.startsWith('bl-') || page.startsWith('icm') ? (
            <SampleBacklogPage key={`samplebacklog-${reloadKey}`} initialView={page} />
          ) : page === 'jobprog' ? (
            <JobProgressPage key={`jobprog-${reloadKey}`} />
          ) : page === 'testprog' ? (
            <TestProgressPage key={`testprog-${reloadKey}`} />
          ) : page === 'taskcal' ? (
            // Task Calendar — same calendar widget as Sample Scheduling's
            // Scheduled Task Tracking page, surfaced here under Task Tracker.
            <ScheduledTaskPage key={`taskcal-${reloadKey}`} pageId="taskcal" />
          ) : page === 'avainsight' ? (
            <AvaInsightPage key={`${page}-${reloadKey}`} />
          ) : page === 'trendanalysis' ? (
            <TrendAnalysisPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'mdlstudies' ? (
            <MdlStudiesPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sampaloc' ? (
            <SamplingAllocationPage key={`${page}-${reloadKey}`} />
          ) : page === 'schedtask' ? (
            <ScheduledTaskPage key={`${page}-${reloadKey}`} pageId="schedtask" />
          ) : page === 'sampreg' ? (
            <SampleRegistrationPage key={`${page}-${reloadKey}`} />
          ) : page === 'sampreceipt' ? (
            <SampleReceiptNotificationPage key={`${page}-${reloadKey}`} />
          ) : page === 'sampphcheck' ? (
            <SampleContainerPhCheckPage key={`${page}-${reloadKey}`} />
          ) : page === 'sample-in' ? (
            <SampleInPage key={`${page}-${reloadKey}`} />
          ) : page === 'sample-out' ? (
            <SampleOutPage key={`${page}-${reloadKey}`} />
          ) : page === 'sample-disposal' ? (
            <SampleDisposalPage key={`${page}-${reloadKey}`} />
          ) : page === 'sample-disposal-history' ? (
            <SampleDisposalHistoryPage key={`${page}-${reloadKey}`} />
          ) : page === 'sample-inout-log' ? (
            <SampleInOutLogPage key={`${page}-${reloadKey}`} />
          ) : page === 'sample-location' ? (
            <SampleLocationPage key={`${page}-${reloadKey}`} />
          ) : page === 'fielddata-entry' ? (
            <FieldDataEntryPage key={`${page}-${reloadKey}`} />
          ) : page === 'fielddata-validation' ? (
            <FieldDataValidationPage key={`${page}-${reloadKey}`} />
          ) : page === 'fielddata-approval' ? (
            <FieldDataApprovalPage key={`${page}-${reloadKey}`} />
          ) : page === 'fielddata' ? (
            <FieldDataTrackingPage key={`${page}-${reloadKey}`} />
          ) : page === 'samptransfer' ? (
            <SampleTransferPage key={`${page}-${reloadKey}`} />
          ) : page === 'regcompliance' ? (
            <RegulatoryCompliancePage key={`${page}-${reloadKey}`} />
          ) : page === 'dwrc-cats' ? (
            <DwrcCategoriesPage key={`${page}-${reloadKey}`} />
          ) : page === 'dwrc-subcats' ? (
            <DwrcSubCategoriesPage key={`${page}-${reloadKey}`} />
          ) : page === 'useractivity' ? (
            <UserActivityPage key={`${page}-${reloadKey}`} />
          ) : page === 'exceptionlog' ? (
            <ExceptionLogPage key={`${page}-${reloadKey}`} />
          ) : page === 'defaultsettings' ? (
            <DefaultSettingsPage key={`${page}-${reloadKey}`} />
          ) : page === 'formsetup' ? (
            <FormVisibilityPage key={`${page}-${reloadKey}`} />
          ) : page === 'audittrailcfg' ? (
            <AuditTrailConfigurationPage key={`${page}-${reloadKey}`} />
          ) : page === 'emailalerts' ? (
            <EmailAlertsPage key={`${page}-${reloadKey}`} />
          ) : page === 'appconfig' ? (
            <AppConfigPage key={`${page}-${reloadKey}`} />
          ) : page === 'clientversion' ? (
            <ClientVersionControlPage key={`${page}-${reloadKey}`} />
          ) : page === 'alpacaappconfig' ? (
            <AlpacaAppConfigPage key={`${page}-${reloadKey}`} />
          ) : page === 'screenautolock' ? (
            <ScreenAutoLockPage key={`${page}-${reloadKey}`} />
          ) : page === 'act-prepflow' ? (
            <ActConfigPage key={`${page}-${reloadKey}`} />
          ) : page === 'workflow-engine' ? (
            <WorkflowConfigListPage key={`${page}-${reloadKey}`} />
          ) : page === 'training-records' ? (
            <TrainingRecordsPage key={`${page}-${reloadKey}`} />
          ) : page === 'control-charts' ? (
            <ControlChartPage key={`${page}-${reloadKey}`} />
          ) : page === 'morlist' ? (
            <MorReportingPage key={`${page}-${reloadKey}`} />
          ) : page === 'mor-template' ? (
            <MorTemplateSetupPage key={`${page}-${reloadKey}`} />
          ) : page === 'morreport' ? (
            <MorAdhocReportingPage key={`morreport-${reloadKey}`} />
          ) : page === 'subouttest' ? (
            <SuboutListPage
              key={`${page}-${reloadKey}`}
              view="test-orders"
              title="Subout Test Order"
              subtitle="Outgoing subcontract orders awaiting shipment, sign-off, or result entry."
            />
          ) : page === 'suboutnotif' ? (
            <SuboutListPage
              key={`${page}-${reloadKey}`}
              view="notifications"
              title="Subout Notification Queue"
              subtitle="Orders whose results are ready but the contract lab hasn't been notified yet."
            />
          ) : page === 'preplqueue' ? (
            <AnalysisListPage key={`${page}-${reloadKey}`} kind="prep-queue" group="Sample Preparation"
              title="Sample Prep Queue"
              subtitle="Test methods with samples waiting on preparation (PrepMethodCount > 0, IsPrepMethodComplete = false)." />
          ) : page === 'prepbatches' ? (
            <AnalysisListPage key={`${page}-${reloadKey}`} kind="prep-batches" group="Sample Preparation"
              title="Sample Prep Batches"
              subtitle="Prep batches in the last few months grouped by tier." />
          ) : page === 'analysisq' ? (
            <AnalysisListPage key={`${page}-${reloadKey}`} kind="analysis-queue" group="Analysis"
              title="Analysis Queue"
              subtitle="Test methods with samples ready for analysis (SignOff = true, not yet analyzed, not assigned to a QC batch)." />
          ) : page === 'resexpress' ? (
            <ResultEntryExpressPage key={`${page}-${reloadKey}`} />
          ) : page === 'reformula' ? (
            // Settings ▸ SDMS ▸ Result Entry Formula (2026-07-28) — bespoke page
            // (Analysis Department Chain pattern): mapped Test+Matrix+Method combos
            // + an Add/Edit Formulas picker. STANDALONE — 'formulalib' (Formula
            // Library, the shared definitions) is its OWN full-CRUD form via the
            // generic ModuleListView/MODULE_CRUD below, not a tab of this page.
            <ResultEntryFormulaPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'qcbatches' ? (
            // onOpenNav lets the QC Batches list's "New" button jump to the
            // Analysis Queue (QC batches are built from analysis-queue rows). The
            // QC Batch detail form opens inline within AnalysisListPage. (Merged 2026-07-07.)
            <AnalysisListPage key={`${page}-${reloadKey}`} kind="qc-batches" group="Analysis"
              title="QC Batches"
              subtitle="Analytical batches in the workflow — Pending Result Entry → Approved."
              onOpenNav={open} />
          ) : page === 'sdms' ? (
            // SDMS Result Entry (OG nav 'Spreadsheet' / view 'SDMS'). Opened blank
            // from the nav (Retrieve a batch); the QC Batch form opens the same
            // screen seeded with a batch.
            <SdmsResultEntryPage key={`${page}-${reloadKey}`} />
          ) : page === 'restrack' ? (
            <AnalysisListPage key={`${page}-${reloadKey}`} kind="result-tracking" group="Analysis"
              title="Result Tracking"
              subtitle="Sample-parameter rows assigned to a QC batch and analyzed (Last month by default)." />
          ) : page === 'sdmsval' ? (
            <AnalysisListPage key={`${page}-${reloadKey}`} kind="result-validation" group="Data Review"
              title="SDMS Result Validation"
              subtitle="Analytical batches awaiting level-2 review (Status = Pending Validation)." />
          ) : page === 'sdmsappr' ? (
            // SDMS Result Approval — batch-level (ALPACA RawDataLevel3BatchReview).
            // Separate from the legacy job-level Result Approval ('rappr'). (Merged 2026-07-07.)
            <AnalysisListPage key={`${page}-${reloadKey}`} kind="sdms-approval" group="Data Review"
              title="SDMS Result Approval"
              subtitle="Analytical batches awaiting SDMS level-3 approval (Status = Pending SDMS Validation)." />
          ) : page === 'rval' ? (
            // Legacy job-level Result Validation (ALPACA QCBatch_ListView_ResultValidation_Filter) —
            // the manual counterpart of 'rappr' below. The list/detail/Validate/Rollback
            // machinery already exists (kind="result-validation-manual" drives
            // ResultValidationDetail in its default mode="validation"); this route was the
            // only missing piece (user report 2026-07-17).
            <AnalysisListPage key={`${page}-${reloadKey}`} kind="result-validation-manual" group="Data Review"
              title="Result Validation"
              subtitle="Analytical batches awaiting level-2 validation (Status = Pending Validation)." />
          ) : page === 'rappr' ? (
            <AnalysisListPage key={`${page}-${reloadKey}`} kind="result-approval" group="Data Review"
              title="Result Approval"
              subtitle="Analytical batches awaiting level-3 approval (Status = Pending Approval)." />
          ) : page === 'custreport' ? (
            // List-view click → per-job detail was REMOVED (2026-06-13).  The
            // only path is: select JobID(s) → Next → combined view (Report /
            // PDF / Save all live there).  No onCustomJobDrill is passed.
            <ReportingListPage key={`${page}-${reloadKey}`} kind="custom"
              title="Custom Reporting"
              subtitle="Every report in the system — list view (Reporting BO, no filter)." />
          ) : page === 'custreportbuilder' ? (
            <CustomReportBuilderPage key={`${page}-${reloadKey}`} />
          ) : page === 'repval' ? (
            <ReportingListPage key={`${page}-${reloadKey}`} kind="validation"
              title="Report Validation"
              subtitle="Reports waiting for level-2 validation (ReportValidatedDate IS NULL)." />
          ) : page === 'repappr' ? (
            <ReportingListPage key={`${page}-${reloadKey}`} kind="approval"
              title="Report Approval"
              subtitle="Reports waiting for level-3 approval (validated, ReportApprovedDate IS NULL)." />
          ) : page === 'morpapp' ? (
            <MorReportingPage key={`${page}-${reloadKey}`} pageId="morpapp" statusFilter={1} breadcrumb="MOR Pending Approval" />
          ) : page === 'repdel' ? (
            <ReportingListPage key={`${page}-${reloadKey}`} kind="delivery"
              title="Report Delivery"
              subtitle="Approved reports at the delivery stage — Pending Delivery and already Sent (ReportApprovedDate IS NOT NULL)." />
          ) : page === 'morpdel' ? (
            // ALPACA LIMS parity: legacy's DrinkingWaterQualityReports_ListView_
            // PendingDelivery criteria is "[Status] = PendingDelivery Or [Status] =
            // Delivered" — both statuses stay visible here, not just still-pending
            // ones (2026-07-17 fix).
            <MorReportingPage key={`${page}-${reloadKey}`} pageId="morpdel" statusFilter={[2, 3]} breadcrumb="MOR Pending Delivery" />
          ) : page === 'reptrack' ? (
            <ReportingListPage key={`${page}-${reloadKey}`} kind="tracking"
              title="Report Tracking"
              subtitle="Full report log with workflow stage badges (validated → approved → delivered)." />
          ) : page === 'batchreport' ? (
            <BatchReportingPage key={`${page}-${reloadKey}`} />
          ) : page === 'edi-import' ? (
            <ExternalDataImportPage key={page} onHome={() => open('home')} />
          ) : page === 'edi-limits' ? (
            <LimitsPage key={page} onHome={() => open('home')} />
          ) : page === 'eddgen' ? (
            <ReportingListPage key={`${page}-${reloadKey}`} kind="edd"
              title="EDD Report Generator"
              subtitle="Electronic Data Deliverable generator history — EDDReportGenerator BO." />
          ) : page === 'eddresult' ? (
            // Separate Spreadsheet View opened by the EDD Report Query popup's OK —
            // tabbed Univer workbook of the generated sheets. (Merged 2026-07-07.)
            <EddSpreadsheetPage key={`${page}-${reloadKey}`} />
          /* 'msr' (Monthly Summary Repository) no longer has a special case —
             it falls through to the shared ModuleListView below (ModuleRegistry
             key 'msr'), which adds the New/Edit dialog, Query panel, Summary and
             AutoGenerate actions (merged team package 2026-07-02).  The earlier
             read-only ReportingListPage kind="msr" view is superseded. */
          ) : page === 'mydetails' ? (
            <MyDetailsPage key={`${page}-${reloadKey}`} />
          ) : page === 'sec-users' ? (
            <UsersAdminPage key={`${page}-${reloadKey}`} />
          ) : page === 'sec-roles' ? (
            <RolesAdminPage key={`${page}-${reloadKey}`} />
          ) : page === 'sec-mac' ? (
            <MacAdminPage key={`${page}-${reloadKey}`} tab="levels" />
          ) : page === 'sec-rolemac' ? (
            <MacAdminPage key={`${page}-${reloadKey}`} tab="matrix" />
          ) : page === 'sec-navmgr' ? (
            <NavigationManagerPage key={`${page}-${reloadKey}`} />
          ) : page === 'servermon' ? (
            <ServerMonitorPage key={`${page}-${reloadKey}`} />
          ) : page === 'servermon-history' ? (
            <ServerHistoryPage key={`${page}-${reloadKey}`} />
          ) : page.startsWith('at-') ? (
            // Audit Trail Log entries — Data Center → Audit Trail Log →
            // {Custom Reporting / MOR Reporting / Sample Registration /
            //  …}.  All route to the same full-page AuditTrailPage which
            // hits /api/v1/audit/trail with the form-name resolved from
            // the page id.  See AuditTrailPage.AT_FORM_BY_ID for the
            // page-id → ALPACA FormName mapping.
            <AuditTrailPage
              key={`${page}-${reloadKey}`}
              pageId={page}
              navTitle={navLabel(page)}
            />
          ) : page === 'acc-paymentterms' ? (
            <PaymentTermsPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'acc-region' ? (
            <RegionPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'maint-versionlog' ? (
            <VersionLogPage key={`${page}-${reloadKey}`} />
          ) : page === 'acc-paymentterms-setup' ? (
            <PaymentTermsSetupPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'adc-chain' ? (
            <AnalysisDepartmentChainPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-city' ? (
            <CityPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-division' ? (
            <CompanyPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-division-department' ? (
            <DivisionDepartmentPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-country' ? (
            <CountryPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-state' ? (
            <StatePage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-department' ? (
            <DepartmentPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-glossary' ? (
            <GlossaryPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-message-editor' ? (
            <MessageEditorPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-position' ? (
            <PositionPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-timezones' ? (
            <TimeZonesPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-language' ? (
            <CurrentLanguagePage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : (
            // Global fallback — ALPACA NavigationItem → ListView parity: ANY nav
            // item without a custom page above opens its list view through the
            // one shared ModuleListView.  Forms with a ModuleRegistry data source
            // show real rows; forms without one show an empty list view (title +
            // toolbar + a "not configured yet" message) instead of a placeholder.
            <ModuleListView
              /* reloadKey folded in so clicking the nav item ALWAYS lands on the
                 LIST view (standing rule 10) — a re-click while the Detail view
                 is open remounts the module back to its list (user 2026-07-02). */
              key={`${page}-${reloadKey}`}
              moduleKey={page}
              group={pageGroups[page] ?? 'Modules'}
              navTitle={navLabel(page)}
              onHome={() => open('home')}
            />
          )}
        </div>
        </BannerContext.Provider>
      </div>
    </div>
  );
}
